import { view, cssGenerator } from '@yola/ws-sdk';
import blocks from 'src/js/modules/blocks';
import { DESKTOP_BREAKPOINT } from 'src/js/modules/extensions/common/constants/breakpoints';
import hasBackgroundContainer from './has-background-container';
import isImageContainer from './is-image-container';
import { getSettings } from '../settings';
import operationTypes from '../constants/operation-types';

const { CONTENT_POSITION: CONTENT_POSITION_CLASS } = cssGenerator.constants.classNames;
const { CONTENT_SCALE: CONTENT_SCALE_ATTRIBUTE } = cssGenerator.constants.attributes;
const { setElementAttribute, setResponsiveClass, setResponsiveAttribute } = view.operations;

// `ws-block` / `ws-background-container` store position as `x% y%` attribute,
// `ws-media-container` stores it as `content-position-[x%_y%]` responsive class.
const toAttributePosition = (value) => String(value).replace('_', ' ');
const toClassPosition = (value) => String(value).replace(' ', '_');

// eslint-disable-next-line consistent-return
const getViewOperation = (operationType, elementId, value, breakpoint = DESKTOP_BREAKPOINT) => {
  const settings = getSettings();
  const element = view.accessors.getLiveElement(elementId);

  const backgroundContainerNode = element.querySelector(settings.backgroundContainer);
  const backgroundContainerNodeId =
    backgroundContainerNode && view.accessors.getLiveElementId(backgroundContainerNode);
  const imageContainerNodeId = view.accessors.getLiveElementId(element);
  switch (operationType) {
    case operationTypes.POSITION: {
      if (blocks.verifiers.isWsBlock(element)) {
        return [
          setElementAttribute,
          [elementId, 'background-position', toAttributePosition(value)],
        ];
      }
      if (hasBackgroundContainer(element)) {
        return [
          setElementAttribute,
          [backgroundContainerNodeId, 'position', toAttributePosition(value)],
        ];
      }
      if (isImageContainer(element)) {
        return [
          setResponsiveClass,
          [imageContainerNodeId, CONTENT_POSITION_CLASS, toClassPosition(value), breakpoint],
        ];
      }
      break;
    }
    case operationTypes.SCALE: {
      if (blocks.verifiers.isWsBlock(element)) {
        return [setElementAttribute, [elementId, 'background-scale', value]];
      }
      if (hasBackgroundContainer(element)) {
        return [setElementAttribute, [backgroundContainerNodeId, 'scale', value]];
      }
      if (isImageContainer(element)) {
        return [
          setResponsiveAttribute,
          [imageContainerNodeId, CONTENT_SCALE_ATTRIBUTE, value, breakpoint],
        ];
      }
      break;
    }
    case operationTypes.SRC: {
      if (blocks.verifiers.isWsBlock(element)) {
        return [setElementAttribute, [elementId, 'background-image', value]];
      }
      if (hasBackgroundContainer(element)) {
        return [setElementAttribute, [backgroundContainerNodeId, 'src', value]];
      }
      if (isImageContainer(element)) {
        const image = element.querySelector('img');
        const imageId = image && view.accessors.getLiveElementId(image);
        return [setElementAttribute, [imageId, 'src', value]];
      }
      break;
    }
    case operationTypes.ORIGIN_SRC: {
      return [setElementAttribute, [elementId, 'origin-src', value]];
    }
    case operationTypes.ORIGIN_WIDTH:
    case operationTypes.ORIGIN_HEIGHT: {
      return [setElementAttribute, [elementId, operationType, value]];
    }
    default: {
      const computedValue = value === true ? '' : value;

      return [setElementAttribute, [elementId, operationType, computedValue]];
    }
  }
};

export default getViewOperation;
